import type { InvoiceDocType, InvoiceStatus } from '@/lib/finance';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Documents commerciaux (facture / devis)
// Modèle unique partagé par les factures et les devis, construit
// autour des 5 blocs légaux :
//   1. En-tête vendeur        2. Bloc client
//   3. Références du document 4. Détail des biens & services
//   5. Totaux & modalités de paiement
// ─────────────────────────────────────────────────────────────

export type DocType = InvoiceDocType;

export interface InvoiceLineDraft {
  description: string;
  quantity: number;
  unit_price: number;
  /** Remise en montant sur la ligne (HT). */
  discount: number;
  item_type: 'product' | 'service' | 'custom';
  product_id: number | null;
}

export interface InvoiceDraft {
  doc_type: DocType;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  customer_address: string;
  customer_ice: string;
  issue_date: string;
  sale_date: string;
  due_date: string;
  reference: string;
  payment_terms: string;
  payment_method: string;
  legal_mentions: string;
  discount: number;
  tax_rate: number;
  notes: string;
  lines: InvoiceLineDraft[];
}

export const DOC_META: Record<DocType, { label: string; plural: string; icon: string; prefix: string; basePath: string; newLabel: string; createLabel: string }> = {
  invoice: {
    label: 'Facture', plural: 'Factures', icon: 'ri-file-text-line', prefix: 'FAC',
    basePath: '/dashboard/finance/factures', newLabel: 'Nouvelle facture', createLabel: 'Créer la facture',
  },
  quote: {
    label: 'Devis', plural: 'Devis', icon: 'ri-file-list-3-line', prefix: 'DEV',
    basePath: '/dashboard/finance/devis', newLabel: 'Nouveau devis', createLabel: 'Créer le devis',
  },
};

/** Statuts spécifiques aux devis (aucune écriture comptable). */
export const QUOTE_STATUS_META: Record<InvoiceStatus, { label: string; className: string }> = {
  draft: { label: 'Brouillon', className: 'bg-background-200/70 text-foreground-600' },
  sent: { label: 'Envoyé', className: 'bg-secondary-100 text-secondary-700' },
  partially_paid: { label: 'En négociation', className: 'bg-accent-100 text-accent-700' },
  paid: { label: 'Accepté', className: 'bg-primary-100 text-primary-700' },
  overdue: { label: 'Expiré', className: 'bg-red-100 text-red-600' },
  cancelled: { label: 'Refusé', className: 'bg-background-200/70 text-foreground-500' },
};

/** Taux de TVA usuels au Maroc + exonération. */
export const VAT_RATES = [
  { value: 20, label: '20 % (normal)' },
  { value: 14, label: '14 %' },
  { value: 10, label: '10 %' },
  { value: 7, label: '7 %' },
  { value: 0, label: 'Exonéré / 0 %' },
];

export const PAYMENT_METHODS = ['Virement bancaire', 'Chèque', 'Espèces', 'Carte bancaire', 'CMI', 'Autre'];

export function emptyLine(): InvoiceLineDraft {
  return { description: '', quantity: 1, unit_price: 0, discount: 0, item_type: 'custom', product_id: null };
}

/** Montant net HT d'une ligne (quantité × prix unitaire − remise ligne). */
export function lineNet(line: InvoiceLineDraft): number {
  const gross = (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);
  return Math.max(0, gross - (Number(line.discount) || 0));
}

export interface DocTotals {
  /** Total HT brut (avant remises de ligne). */
  gross: number;
  /** Remises appliquées sur les lignes. */
  lineDiscounts: number;
  /** Total HT net des lignes. */
  subtotal: number;
  /** Remise globale du document. */
  globalDiscount: number;
  /** Base taxable (HT − remise globale). */
  taxable: number;
  taxRate: number;
  tax: number;
  total: number;
}

export function computeTotals(lines: InvoiceLineDraft[], globalDiscount: number, taxRate: number): DocTotals {
  const gross = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const lineDiscounts = lines.reduce((s, l) => s + (Number(l.discount) || 0), 0);
  const subtotal = Math.max(0, gross - lineDiscounts);
  const disc = Math.max(0, Number(globalDiscount) || 0);
  const taxable = Math.max(0, subtotal - disc);
  const rate = Number(taxRate) || 0;
  const tax = (taxable * rate) / 100;
  return { gross, lineDiscounts, subtotal, globalDiscount: disc, taxable, taxRate: rate, tax, total: taxable + tax };
}

/** Numéro provisoire quand le socle de numérotation est indisponible. */
export function buildFallbackNumber(docType: DocType, seq: number): string {
  const year = new Date().getFullYear();
  const prefix = DOC_META[docType].prefix;
  return `${prefix}-${year}-${String(seq).padStart(5, '0')}`;
}

/** Délai légal de paiement proposé par défaut (30 jours). */
export function dateInDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}